import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { tourRegistry } from '../tours'
import type { TourStore } from '../types'

export const useTourStore = create<TourStore>()(
  persist(
    (set, get) => ({
      // Initial state
      currentTour: null,
      stepIndex: 0,
      run: false,
      completedTours: new Set<string>(),
      isTransitioning: false,
      awaitingTarget: null,

      startTour: (tourId: string, initialStepIndex = 0) => {
        const tour = tourRegistry[tourId]
        if (!tour) {
          return
        }

        const targetStep = tour.steps[initialStepIndex]
        if (targetStep?.onStepEnter) {
          targetStep.onStepEnter()
        }

        set({
          currentTour: tourId,
          stepIndex: initialStepIndex,
          run: true,
          isTransitioning: false,
          awaitingTarget: null,
        })
      },

      // Advance to next step
      nextStep: () => {
        const { currentTour, stepIndex } = get()
        if (!currentTour) return

        const tour = tourRegistry[currentTour]
        if (!tour) return

        const currentStep = tour.steps[stepIndex]

        // Execute navigation action if exists
        if (currentStep?.navigationAction) {
          currentStep.navigationAction()
        }

        const nextIndex = stepIndex + 1

        if (nextIndex >= tour.steps.length) {
          get().completeTour()
        } else {
          const nextStep = tour.steps[nextIndex]

          // If next step waits for target, mark as awaiting
          if (nextStep?.waitForTarget) {
            set({
              stepIndex: nextIndex,
              awaitingTarget: nextStep.target,
              run: false, // Pause tour until target appears
            })
          } else {
            if (nextStep?.onStepEnter) {
              nextStep.onStepEnter()
            }

            set({ stepIndex: nextIndex })
          }
        }
      },

      prevStep: () => {
        const { currentTour, stepIndex } = get()
        if (!currentTour || stepIndex <= 0) return

        const tour = tourRegistry[currentTour]
        if (!tour) return

        const prevIndex = stepIndex - 1
        const prevStep = tour.steps[prevIndex]

        if (prevStep?.onStepEnter) {
          prevStep.onStepEnter()
        }

        set({ stepIndex: prevIndex })
      },

      manualStepAdvance: () => {
        const { currentTour, stepIndex } = get()
        if (!currentTour) return

        const tour = tourRegistry[currentTour]
        if (!tour) return

        const currentStep = tour.steps[stepIndex]

        if (currentStep?.onStepComplete) {
          currentStep.onStepComplete()
        }

        get().nextStep()
      },

      handleTargetMounted: (target: string) => {
        const { awaitingTarget, currentTour, stepIndex } = get()

        if (awaitingTarget === target && currentTour) {
          const tour = tourRegistry[currentTour]
          const currentStep = tour?.steps[stepIndex]

          if (currentStep?.onStepEnter) {
            currentStep.onStepEnter()
          }

          set({
            awaitingTarget: null,
            run: true, // Resume tour
          })
        }
      },

      // Complete current tour
      completeTour: () => {
        const { currentTour, completedTours } = get()
        if (!currentTour) return

        const tour = tourRegistry[currentTour]
        const newCompletedTours = new Set(completedTours)
        newCompletedTours.add(currentTour)

        set({
          currentTour: null,
          stepIndex: 0,
          run: false,
          completedTours: newCompletedTours,
          isTransitioning: false,
          awaitingTarget: null,
        })
      },

      // Auxiliary setters
      setAwaitingTarget: (target: string | null) => set({ awaitingTarget: target }),

      // Set completed tours (for Firebase sync)
      setCompletedTours: (tours: Set<string>) => set({ completedTours: tours }),

      // Complete reset
      resetAllTours: () => {
        set({
          currentTour: null,
          stepIndex: 0,
          run: false,
          completedTours: new Set(),
          isTransitioning: false,
          awaitingTarget: null,
        })
      },
    }),
    {
      name: 'rtsc-tour-store',
      partialize: (state) => ({
        completedTours: Array.from(state.completedTours),
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.completedTours) {
          state.completedTours = new Set(state.completedTours as unknown as string[])
        }
      },
    },
  ),
)
